// Interactive Quattro tutorial — animated mini-boards + tap-to-progress.
//
// Replaces the old text-only `HowToPlay.jsx` and the 3-slide `TutorialOverlay`
// with one shared 6-step walkthrough. The same component is opened both:
//   • automatically on first visit (`open` controlled by Lobby state)
//   • on tap of the lobby "How to Play" button (replay any time)
//
// Each step shows a single sentence + a visual demo. Steps 3 & 4 invite the
// player to tap the mini-board so they build muscle memory, but a Next button
// is always available so impatient players can blast through.
//
// Goal: a player who has never seen Quattro should understand placement,
// stacking, and specials in under 90 seconds.
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { sfx } from "../lib/sound";

const TUTORIAL_KEY = "quattro_tutorial_seen_v2";

export function hasSeenTutorial() {
  try { return !!localStorage.getItem(TUTORIAL_KEY); } catch { return false; }
}
export function markTutorialSeen() {
  try { localStorage.setItem(TUTORIAL_KEY, "1"); } catch {}
}

// ---- Player palette (mirrors lib/quattro.js so the demo looks identical) ----
const YOU = "#FFD700";   // Yellow ♣
const RIVAL = "#FF2A2A"; // Red ♥
const COLD = "#00E5FF";  // Blue ♦
const ACE = "#B537F2";   // Purple ♠

// ===== Tiny card primitive used inside every demo board =====
function MiniCard({ color = YOU, power = 5, suit = "♣", size = 44, locked = false, label = null, glowing = false }) {
  return (
    <motion.div
      animate={glowing ? { boxShadow: [`0 0 0px ${color}88`, `0 0 22px ${color}cc`, `0 0 0px ${color}88`] } : {}}
      transition={glowing ? { duration: 1.1, repeat: Infinity, ease: "easeInOut" } : {}}
      style={{
        width: size, height: size * 1.32,
        borderRadius: 7,
        background: locked
          ? "repeating-linear-gradient(45deg, #2a2a2a, #2a2a2a 4px, #444 4px, #444 8px)"
          : `linear-gradient(160deg, ${color}, #111)`,
        border: `2px solid ${color}`,
        color: "#0B0B0B",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-display, system-ui)",
        fontWeight: 900,
        fontSize: size * 0.42,
        boxShadow: `0 4px 14px ${color}55, inset 0 0 8px ${color}44`,
        userSelect: "none",
      }}
    >
      {locked ? <span style={{ color: "#FFD700", fontSize: size * 0.5 }}>🔒</span> : (
        <>
          <span style={{ color: "#0B0B0B", lineHeight: 1 }}>{label != null ? label : power}</span>
          <span style={{ fontSize: size * 0.22, marginTop: 2, color: "#0B0B0B" }}>{suit}</span>
        </>
      )}
    </motion.div>
  );
}

// ===== Empty cell (tappable when `onTap` provided) =====
function EmptyCell({ size = 44, hint = false, onTap = null, blocked = false }) {
  return (
    <button
      type="button"
      onClick={onTap || undefined}
      disabled={!onTap}
      style={{
        width: size, height: size * 1.32,
        borderRadius: 7,
        background: hint ? "rgba(61,220,151,0.18)" : "rgba(255,255,255,0.04)",
        border: hint ? "1.5px dashed rgba(61,220,151,0.7)" : "1.5px dashed rgba(255,255,255,0.12)",
        cursor: onTap ? "pointer" : "default",
        opacity: blocked ? 0.3 : 1,
      }}
    />
  );
}

// ============================================================================
// Step 1 — The goal: 4-in-a-row
// Auto-cycles through three winning lines (horizontal → vertical → diagonal)
// ============================================================================
function GoalDemo() {
  const [phase, setPhase] = useState(0); // 0,1,2 = horiz, vert, diag
  useEffect(() => {
    const t = setInterval(() => setPhase((p) => (p + 1) % 3), 2200);
    return () => clearInterval(t);
  }, []);

  // Each phase highlights 4 cells out of a 4x4 mini grid.
  const PATTERNS = [
    // horizontal row 1
    [[1,0],[1,1],[1,2],[1,3]],
    // vertical col 1
    [[0,2],[1,2],[2,2],[3,2]],
    // diagonal
    [[0,0],[1,1],[2,2],[3,3]],
  ];
  const lit = new Set(PATTERNS[phase].map(([r,c]) => `${r},${c}`));

  return (
    <div className="grid gap-1.5 mx-auto" style={{ gridTemplateColumns: "repeat(4, 1fr)", width: 220 }}>
      {Array.from({ length: 16 }).map((_, idx) => {
        const r = Math.floor(idx / 4), c = idx % 4;
        const on = lit.has(`${r},${c}`);
        return (
          <div key={idx} style={{ display: "flex", justifyContent: "center" }}>
            {on ? (
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: PATTERNS[phase].findIndex(([rr,cc]) => rr===r && cc===c) * 0.15 }}
              >
                <MiniCard color={YOU} suit="♣" power={5 + Math.floor(Math.random()*4)} size={42} glowing />
              </motion.div>
            ) : (
              <EmptyCell size={42} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Step 2 — Card anatomy (tap to change power)
// ============================================================================
function CardAnatomyDemo() {
  const [pwr, setPwr] = useState(5);
  return (
    <div className="flex items-center gap-5 justify-center">
      <button
        type="button"
        className="rounded-full px-3 py-1 text-xl"
        style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)" }}
        onClick={() => { sfx.click(); setPwr((p) => Math.max(1, p-1)); }}
        data-testid="tutorial-pwr-down"
      >−</button>
      <motion.div key={pwr} initial={{ scale: 0.85, rotate: -3 }} animate={{ scale: 1, rotate: 0 }}>
        <MiniCard color={YOU} suit="♣" power={pwr} size={88} glowing />
      </motion.div>
      <button
        type="button"
        className="rounded-full px-3 py-1 text-xl"
        style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)" }}
        onClick={() => { sfx.click(); setPwr((p) => Math.min(9, p+1)); }}
        data-testid="tutorial-pwr-up"
      >+</button>
    </div>
  );
}

// ============================================================================
// Step 3 — Placement (tap a green hint cell)
// ============================================================================
function PlacementDemo({ onComplete }) {
  const [placed, setPlaced] = useState(null); // {r,c}
  // Hint cells = 8-way neighbors of the centre card at (1,1).
  const hints = new Set([
    "0,0","0,1","0,2",
    "1,0","1,2",
    "2,0","2,1","2,2",
  ]);
  const onTap = (r, c) => {
    sfx.click();
    setPlaced({ r, c });
    setTimeout(() => onComplete?.(), 600);
  };
  return (
    <div className="grid gap-1.5 mx-auto" style={{ gridTemplateColumns: "repeat(3, 1fr)", width: 200 }}>
      {Array.from({ length: 9 }).map((_, idx) => {
        const r = Math.floor(idx / 3), c = idx % 3;
        const isCentre = r === 1 && c === 1;
        const isHint = hints.has(`${r},${c}`);
        const isPlaced = placed && placed.r === r && placed.c === c;
        return (
          <div key={idx} style={{ display: "flex", justifyContent: "center" }}>
            {isCentre ? (
              <MiniCard color={RIVAL} suit="♥" power={4} size={56} />
            ) : isPlaced ? (
              <motion.div initial={{ scale: 0.4, rotate: -8, opacity: 0 }} animate={{ scale: 1, rotate: 0, opacity: 1 }}>
                <MiniCard color={YOU} suit="♣" power={6} size={56} glowing />
              </motion.div>
            ) : (
              <EmptyCell size={56} hint={isHint && !placed} onTap={isHint && !placed ? () => onTap(r, c) : null} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Step 4 — Stacking (tap the rival "5" with your "8")
// ============================================================================
function StackDemo({ onComplete }) {
  const [stacked, setStacked] = useState(false);
  const onTap = () => {
    if (stacked) return;
    sfx.click();
    setStacked(true);
    setTimeout(() => onComplete?.(), 700);
  };
  return (
    <div className="flex items-center justify-center gap-6 py-2">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1">YOU</div>
        <MiniCard color={YOU} suit="♣" power={8} size={62} glowing={!stacked} />
      </div>
      <span className="text-2xl text-white/35">→</span>
      <div className="text-center relative">
        <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1">RIVAL</div>
        <button
          type="button"
          onClick={onTap}
          disabled={stacked}
          style={{ background: "transparent", border: "none", padding: 0, cursor: stacked ? "default" : "pointer" }}
          data-testid="tutorial-stack-target"
        >
          <div style={{ position: "relative" }}>
            <MiniCard color={RIVAL} suit="♥" power={5} size={62} />
            {stacked && (
              <motion.div
                initial={{ y: -60, opacity: 0, rotate: -8 }} animate={{ y: 0, opacity: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 280, damping: 18 }}
                style={{ position: "absolute", inset: 0 }}
              >
                <MiniCard color={YOU} suit="♣" power={8} size={62} glowing />
              </motion.div>
            )}
          </div>
        </button>
        <div className="text-[10px] mt-1" style={{ color: stacked ? "#3DDC97" : "rgba(255,255,255,0.45)" }}>
          {stacked ? "stacked!" : "8 > 5  · tap"}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 5 — Specials (tap each special to see its effect)
// ============================================================================
const SPECIALS = [
  { id: "power_up",      icon: "▲", label: "Power Up",   desc: "Boost your top card by +3.", color: "#3DDC97" },
  { id: "power_down",    icon: "▼", label: "Power Down", desc: "Cut an opponent's card by 3.", color: "#FF8A8A" },
  { id: "blocker",       icon: "✦", label: "Blocker",    desc: "Lock an empty cell forever.", color: "#FFD700" },
  { id: "color_switcher",icon: "↔", label: "Switcher",   desc: "Flip an enemy card to your color.", color: "#B537F2" },
];
function SpecialsDemo() {
  const [active, setActive] = useState("power_up");
  const cur = SPECIALS.find((s) => s.id === active);
  return (
    <div>
      <div className="grid grid-cols-4 gap-2 mb-3">
        {SPECIALS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => { sfx.click(); setActive(s.id); }}
            data-testid={`tutorial-special-${s.id}`}
            style={{
              padding: "10px 4px",
              borderRadius: 12,
              border: active === s.id ? `2px solid ${s.color}` : "1px solid rgba(255,255,255,0.12)",
              background: active === s.id ? `${s.color}22` : "rgba(255,255,255,0.04)",
              color: s.color,
              fontSize: 22,
              fontWeight: 900,
              transition: "all 0.2s",
            }}
          >{s.icon}</button>
        ))}
      </div>
      <div
        className="rounded-xl p-3 text-center text-sm"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: `1px solid ${cur.color}55`,
          minHeight: 56,
        }}
      >
        <div className="font-display font-bold mb-1" style={{ color: cur.color }}>{cur.label}</div>
        <div className="text-white/75 text-xs">{cur.desc}</div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 6 — Ready
// ============================================================================
function ReadyDemo() {
  return (
    <div className="grid gap-1.5 mx-auto" style={{ gridTemplateColumns: "repeat(4, 1fr)", width: 200 }}>
      {Array.from({ length: 16 }).map((_, idx) => {
        const colors = [YOU, COLD, RIVAL, ACE];
        const c = colors[idx % 4];
        const suits = ["♣", "♦", "♥", "♠"];
        const s = suits[idx % 4];
        return (
          <div key={idx} style={{ display: "flex", justifyContent: "center" }}>
            <motion.div
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: idx * 0.04, type: "spring", stiffness: 280 }}
            >
              <MiniCard color={c} suit={s} power={1 + (idx % 9)} size={36} />
            </motion.div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================
const STEPS = [
  {
    id: "goal",
    eyebrow: "1. THE GOAL",
    title: "Line up four of yours",
    body: "Get 4 of your color in a row — across, down, or diagonal. First to do it wins.",
    Demo: GoalDemo,
  },
  {
    id: "card",
    eyebrow: "2. THE CARD",
    title: "Each card has a power 1 – 9",
    body: "Higher power beats lower. Tap + or − to see it change.",
    Demo: CardAnatomyDemo,
  },
  {
    id: "place",
    eyebrow: "3. PLACE NEXT TO ANY CARD",
    title: "Tap a green cell to place yours",
    body: "Cards must touch an existing one — even diagonally.",
    Demo: PlacementDemo,
    needsTap: true,
  },
  {
    id: "stack",
    eyebrow: "4. STACK TO OVERPOWER",
    title: "Higher power covers a card",
    body: "Tap the rival's 5 — your 8 wins it.",
    Demo: StackDemo,
    needsTap: true,
  },
  {
    id: "specials",
    eyebrow: "5. SPECIAL CARDS",
    title: "Four game-changers",
    body: "Tap each icon to see what it does.",
    Demo: SpecialsDemo,
  },
  {
    id: "ready",
    eyebrow: "6. YOU'RE READY",
    title: "Play your first hand",
    body: "It only takes a few minutes.  Good luck!",
    Demo: ReadyDemo,
  },
];

export default function InteractiveTutorial({ open, onClose, autoplayOnFinish = true }) {
  const [idx, setIdx] = useState(0);
  const [completedTap, setCompletedTap] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (open) { setIdx(0); setCompletedTap(false); }
  }, [open]);

  useEffect(() => {
    setCompletedTap(false);
  }, [idx]);

  const cur = STEPS[idx];
  const isLast = idx === STEPS.length - 1;

  const finish = (startGame = false) => {
    markTutorialSeen();
    onClose?.();
    if (startGame) nav("/play");
  };

  const next = () => {
    sfx.click();
    if (isLast) finish(autoplayOnFinish);
    else setIdx(idx + 1);
  };
  const back = () => { sfx.click(); setIdx(Math.max(0, idx - 1)); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-3"
          style={{ background: "rgba(0,0,0,0.88)" }}
          data-testid="interactive-tutorial"
        >
          <motion.div
            initial={{ scale: 0.94, y: 18 }} animate={{ scale: 1, y: 0 }}
            className="glass max-w-md w-full rounded-3xl relative flex flex-col"
            style={{ borderColor: "rgba(255,215,0,0.45)", maxHeight: "94vh" }}
          >
            {/* Header — Skip + step counter, always visible */}
            <div className="px-6 pt-5 pb-2 flex items-center justify-between flex-shrink-0">
              <div className="text-[10px] uppercase tracking-wider text-white/55">
                Step {idx + 1} of {STEPS.length}
              </div>
              <button
                className="text-xs text-white/55 hover:text-white"
                onClick={() => finish(false)}
                data-testid="tutorial-skip-btn"
              >Skip ✕</button>
            </div>

            {/* Body — scrollable on small phones */}
            <div className="px-6 pb-3 overflow-y-auto" style={{ flex: "1 1 auto", minHeight: 0 }}>
              <motion.div key={cur.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                <div className="label-eyebrow mb-1.5" style={{ color: "#FFD700" }} data-testid="tutorial-eyebrow">
                  {cur.eyebrow}
                </div>
                <h2 className="font-display font-black text-2xl mb-1.5" data-testid="tutorial-title">
                  {cur.title}
                </h2>
                <p className="text-white/75 text-sm mb-5 leading-relaxed">{cur.body}</p>

                {/* Interactive demo */}
                <div className="py-3 mb-2">
                  <cur.Demo onComplete={cur.needsTap ? () => setCompletedTap(true) : undefined} />
                </div>
              </motion.div>
            </div>

            {/* Footer — progress dots + Back/Next */}
            <div className="px-6 pt-2 pb-5 flex-shrink-0 border-t border-white/5">
              <div className="flex items-center justify-center gap-1.5 mb-3">
                {STEPS.map((_, i) => (
                  <span
                    key={i}
                    className="rounded-full transition-all"
                    style={{
                      width: i === idx ? 22 : 7, height: 7,
                      background: i < idx ? "#3DDC97" : i === idx ? "#FFD700" : "rgba(255,255,255,0.18)",
                    }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-ghost flex-shrink-0"
                  style={{ minWidth: 70, opacity: idx === 0 ? 0.4 : 1 }}
                  onClick={back}
                  disabled={idx === 0}
                  data-testid="tutorial-back-btn"
                >Back</button>
                <button
                  type="button"
                  className="btn-primary flex-1"
                  onClick={next}
                  data-testid="tutorial-next-btn"
                  style={{ background: "#FFD700", color: "#0B0B0B" }}
                >
                  {isLast ? "Start Playing" : (cur.needsTap && completedTap ? "Got it · Next" : "Next")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
